import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, FilePen, ShieldCheck } from "lucide-react";
import { ProjectAccessDenied, useProject } from "@lumanity/ui/projects";
import { ErrorBanner, toast } from "@lumanity/ui/primitives";
import PageLayout from "@/components/PageLayout";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import WizardStepper, { type WizardStep } from "@/components/WizardStepper";
import { FirstDraft, useStartFirstDraft } from "@/features/draft";
import { OUTPUT_TYPE_LABELS, OutputBrief, useOutput } from "@/features/outputs";

type Stage = "brief" | "first_draft";

// The output workflow; Senior QC lights up when it's built.
const workflowSteps = (draftStarted: boolean): WizardStep[] => [
  { id: "brief", label: "Brief", icon: ClipboardList },
  { id: "first_draft", label: "First draft", icon: FilePen, disabled: !draftStarted },
  { id: "senior_qc", label: "Senior QC", icon: ShieldCheck, disabled: true },
];

/** One output (abstract / manuscript / poster) of a project: its workflow stepper and the current stage. */
const OutputPage = () => {
  const { projectId = "", outputId = "" } = useParams<{ projectId: string; outputId: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const { data: output, isLoading, error } = useOutput(outputId);
  const [searchParams, setSearchParams] = useSearchParams();
  const startFirstDraft = useStartFirstDraft(outputId);
  const [draftDirty, setDraftDirty] = useState(false);
  const [pendingStage, setPendingStage] = useState<Stage | null>(null);

  if (error) {
    return (
      <PageLayout>
        {/not found|404|403/i.test(error.message) ? (
          <ProjectAccessDenied onBack={() => navigate(`/projects/${projectId}`)} backLabel="Back to the project" />
        ) : (
          <ErrorBanner message={`Could not load the output: ${error.message}`} />
        )}
      </PageLayout>
    );
  }
  if (isLoading || !output) {
    return (
      <PageLayout>
        <div className="text-muted-foreground py-12 text-center">Loading output...</div>
      </PageLayout>
    );
  }

  const typeLabel = OUTPUT_TYPE_LABELS[output.type] ?? output.type;
  const draftStarted = output.stage !== "brief";
  // ?stage= picks the view; the Brief's own ?step= means the Brief; otherwise the output's current stage.
  const requested = searchParams.get("stage");
  const view: Stage = !draftStarted || requested === "brief" || (!requested && searchParams.has("step")) ? "brief" : "first_draft";

  const show = (stage: Stage) => {
    setDraftDirty(false);
    setSearchParams({ stage });
  };
  const onStepClick = (index: number) => {
    const stage = workflowSteps(draftStarted)[index].id as Stage;
    if (stage === view) return;
    if (view === "first_draft" && draftDirty) setPendingStage(stage);
    else show(stage);
  };
  const continueToOutline = () =>
    startFirstDraft.mutate(undefined, {
      onSuccess: () => show("first_draft"),
      onError: (e) => toast({ title: "Could not start the first draft", description: e.message, variant: "destructive" }),
    });

  return (
    <PageLayout>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div className="min-w-0">
          <Link
            to={`/projects/${projectId}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand-teal mb-1"
          >
            <ArrowLeft className="w-3 h-3" /> Project
          </Link>
          <h1 className="text-2xl font-bold text-brand-navy truncate">{project?.name ?? " "}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {typeLabel}
            {output.title && ` · ${output.title}`} · Kantata task <span className="font-medium text-brand-navy">{output.kantata_task_title}</span>
          </p>
        </div>

        <div className="flex items-center border text-sm font-medium" role="group" aria-label="Switch view">
          <Link to={`/projects/${projectId}?step=evidence`} className="px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            Evidence Base
          </Link>
          <span className="px-3 py-1.5 bg-brand-navy text-white" aria-current="page">
            {typeLabel}
          </span>
        </div>
      </div>

      <WizardStepper
        steps={workflowSteps(draftStarted)}
        activeIndex={view === "brief" ? 0 : 1}
        completedIndices={draftStarted ? [0] : []}
        onStepClick={onStepClick}
        variant="compact"
        className="py-5 mb-4 border-y"
      />

      {view === "brief" ? <OutputBrief output={output} onContinueToOutline={continueToOutline} /> : <FirstDraft output={output} onDirtyChange={setDraftDirty} />}
      <UnsavedChangesDialog
        open={pendingStage !== null}
        onCancel={() => setPendingStage(null)}
        onConfirm={() => {
          if (pendingStage) show(pendingStage);
          setPendingStage(null);
        }}
      />
    </PageLayout>
  );
};

export default OutputPage;
